import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Dialog, DialogContent, IconButton, TextField, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { eventAPI, userAPI, organizationAPI } from '../../actions/imsServer';
import dayjs from 'dayjs';

import ContentArea from '../../components/contentArea';
import Toggle from '../../components/toggle';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import { Point } from '../../assets/images';
import Popup from '../../components/popup';
import { API_PAGE_SIZE } from '../../constants';

const ITEMS_PER_PAGE = API_PAGE_SIZE;

export const EventUser = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const [selectedCategory, setSelectedCategory] = useState(0); // 0: 진행중, 1: 완료된
    const [mainOrganizationId, setMainOrganizationId] = useState(null);

    // 응모하기 팝업 관련 상태
    const [applyDialogOpen, setApplyDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [applyQuantity, setApplyQuantity] = useState(1);
    const [userPoint, setUserPoint] = useState(0);
    const [organizationStatistics, setOrganizationStatistics] = useState(null);
    const [mainOrganizationName, setMainOrganizationName] = useState(null);

    // 에러 팝업 관련 상태
    const [errorPopupOpen, setErrorPopupOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const categories = [t('in_progress'), t('completed')];

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo') || '{}'));

    // 기간 필터링 관련 상태
    const [period, setPeriod] = useState('1m');
    const periodOptions = [
        { label: t('1_month'), value: '1m' },
        { label: t('3_months'), value: '3m' },
        { label: t('6_months'), value: '6m' },
        { label: t('1_year'), value: '1y' },
        { label: t('all'), value: 'all' },
    ];

    // 기간에 따른 최소 날짜 계산
    const getMinDate = useCallback((periodValue) => {
        const now = dayjs();
        switch (periodValue) {
            case '1m':
                return now.subtract(1, 'month').format('YYYY-MM-DD');
            case '3m':
                return now.subtract(3, 'month').format('YYYY-MM-DD');
            case '6m':
                return now.subtract(6, 'month').format('YYYY-MM-DD');
            case '1y':
                return now.subtract(1, 'year').format('YYYY-MM-DD');
            case 'all':
                return null; // 전체 기간은 날짜 필터 없음
            default:
                return now.subtract(1, 'month').format('YYYY-MM-DD');
        }
    }, []);

    // 이벤트 데이터 가져오기 (무한스크롤)
    const fetchEvents = useCallback(async (reset = false) => {
        if (isLoadingMore && !reset) return;

        const currentOffset = reset ? 0 : offsetRef.current;

        if (!meInfo.id) {
            setLoading(false);
            return;
        }

        if (reset) {
            setLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            // 진행중/완료 필터: status로 판단
            const eventParams = {
                limit: ITEMS_PER_PAGE,
                offset: currentOffset
            };

            if (selectedCategory === 0) {
                // 진행중: ongoing만
                eventParams.status = 'ongoing';
            } else {
                // 완료된: finishing만
                eventParams.status = 'finishing';
                // 완료된 탭일 때 기간 필터링 적용
                const minDate = getMinDate(period);
                if (minDate) {
                    eventParams.startDate = minDate;
                }
            }

            const eventsResponse = await eventAPI.getEvents(eventParams);

            let eventsData = eventsResponse?.data?.events || eventsResponse?.events || [];
            const total = eventsResponse?.data?.total || eventsResponse?.total || 0;

            // 이벤트 ID 목록 추출
            const eventIds = eventsData.map(event => event.id).filter(id => id != null);

            // 이벤트 참여 데이터를 한 번에 가져오기
            let allUserParticipations = [];
            if (eventIds.length > 0) {
                try {
                    // 먼저 total을 가져오기
                    let participationCountResponse = await eventAPI.getEventParticipations({
                        userId: meInfo.id,
                        eventId: eventIds.join(','),
                        state: '0,2',
                    });

                    const participationTotal = participationCountResponse?.data?.total || 0;

                    if (participationTotal > API_PAGE_SIZE) {
                        participationCountResponse = await eventAPI.getEventParticipations({
                            userId: meInfo.id,
                            eventId: eventIds.join(','),
                            state: '0,2',
                            limit: participationTotal
                        });
                    }
                    allUserParticipations = participationCountResponse?.data?.eventParticipations || [];
                } catch (err) {
                    console.error('사용자 참여 데이터 가져오기 실패:', err);
                }
            }

            // 각 이벤트에 대한 데이터 매핑
            const eventsWithParticipations = await Promise.all(
                eventsData.map(async (event) => {
                    try {
                        // 미리 가져온 참여 데이터에서 해당 이벤트의 데이터 찾기
                        const participation = allUserParticipations.filter(p => p.eventId === event.id);
                        // 해당 이벤트의 내 응모수 합산
                        const myUsedPoint = participation.reduce((sum, p) => sum + (p.usedPoint || 0), 0);

                        // 완료된 이벤트인 경우 당첨자 가져오기 (초기 100개)
                        let winners = [];
                        let rankingWinners = []; // 랭킹제 당첨 기관
                        let winnersOffset = 0;
                        let winnersHasMore = false;
                        let winnersTotal = 0;
                        let winnersRawData = []; // 랭킹제용 원본 데이터
                        if (selectedCategory === 1) {
                            try {
                                const winnersResponse = await eventAPI.getEventParticipations({
                                    eventId: event.id,
                                    expand: 'user',
                                    state: 2, // 당첨자
                                    offset: 0,
                                    limit: ITEMS_PER_PAGE,
                                });

                                const winnersData = winnersResponse?.data?.eventParticipations ||
                                    winnersResponse?.eventParticipations ||
                                    winnersResponse?.data || [];
                                winnersTotal = winnersResponse?.data?.total || winnersResponse?.total || 0;
                                winnersOffset = winnersData.length;
                                winnersHasMore = winnersData.length < winnersTotal && winnersData.length === ITEMS_PER_PAGE;

                                if (event.type === 0) {
                                    // 추첨제: 개인별 당첨자
                                    winners = Array.isArray(winnersData) ? winnersData.map(w => {
                                        // loginId를 이메일로 사용
                                        const email = w.user?.loginId || w.loginId || '';
                                        const name = w.user?.name || w.name || '';

                                        return {
                                            name: name,
                                            email: email,
                                        };
                                    }) : [];
                                } else if (event.type === 1) {
                                    // 랭킹제: 원본 데이터 저장 및 기관별 그룹화
                                    winnersRawData = Array.isArray(winnersData) ? winnersData : [];
                                    const orgMap = new Map();
                                    const totalUsedPoint = event.totalParticipationUsedPoint || 0;

                                    winnersRawData.forEach(w => {
                                        const orgId = w.organizationId;
                                        const orgName = w.organization?.name || w.organizationName || t('no_organization');

                                        if (!orgMap.has(orgId)) {
                                            orgMap.set(orgId, {
                                                organizationId: orgId,
                                                organizationName: orgName,
                                                totalPoint: 0,
                                                topUser: null,
                                                topUserPoint: 0,
                                                topUserEmail: '',
                                            });
                                        }

                                        const orgData = orgMap.get(orgId);
                                        const usedPoint = w.usedPoint || 0;
                                        orgData.totalPoint += usedPoint;

                                        // 기관 내 1위 사용자 업데이트
                                        if (usedPoint > orgData.topUserPoint) {
                                            // loginId를 이메일로 사용
                                            const email = w.user?.loginId || w.loginId || '';
                                            const name = w.user?.name || w.name || '';

                                            orgData.topUser = name;
                                            orgData.topUserPoint = usedPoint;
                                            orgData.topUserEmail = email;
                                        }
                                    });

                                    // 기관별 데이터를 배열로 변환하고 포인트 순으로 정렬
                                    rankingWinners = Array.from(orgMap.values())
                                        .sort((a, b) => b.totalPoint - a.totalPoint)
                                        .map((org, index) => ({
                                            rank: index + 1,
                                            organizationName: org.organizationName,
                                            totalPoint: org.totalPoint,
                                            percentage: totalUsedPoint > 0 ? Math.round((org.totalPoint / totalUsedPoint) * 100) : 0,
                                            topUser: org.topUser,
                                            topUserEmail: org.topUserEmail,
                                            topUserPoint: org.topUserPoint,
                                        }));
                                }
                            } catch (err) {
                                console.error('당첨자 데이터 가져오기 실패:', err);
                            }
                        }

                        return {
                            id: event.id,
                            name: event.name || '',
                            type: event.type || 0,
                            startDate: event.startDate || '',
                            endDate: event.endDate || '',
                            totalParticipationUsedPoint: event.totalParticipationUsedPoint || 0,
                            usedPoint: myUsedPoint,
                            imageUrl: event.file || event.imageUrl || null,
                            winners: winners,
                            rankingWinners: rankingWinners,
                            winnersOffset: winnersOffset,
                            winnersHasMore: winnersHasMore,
                            winnersRawData: winnersRawData,
                            winnersTotal: winnersTotal,
                            isLoadingMoreWinners: false,
                        };
                    } catch (err) {
                        console.error(`이벤트 ${event.id} 참여 데이터 가져오기 실패:`, err);
                        return {
                            id: event.id,
                            name: event.name || '',
                            type: event.type || 0,
                            startDate: event.startDate || '',
                            endDate: event.endDate || '',
                            totalParticipationUsedPoint: event.totalParticipationUsedPoint || 0,
                            usedPoint: 0,
                            imageUrl: event.file || event.imageUrl || null,
                            winners: [],
                            rankingWinners: [],
                            winnersOffset: 0,
                            winnersHasMore: false,
                            winnersRawData: [],
                            winnersTotal: 0,
                            isLoadingMoreWinners: false,
                        };
                    }
                })
            );

            if (reset) {
                setEvents(eventsWithParticipations);
                offsetRef.current = eventsData.length;
            } else {
                setEvents(prev => [...prev, ...eventsWithParticipations]);
                const newOffset = currentOffset + eventsData.length;
                offsetRef.current = newOffset;
            }

            // 더 불러올 데이터가 있는지 확인
            const finalOffset = reset ? eventsData.length : currentOffset + eventsData.length;
            setHasMore(finalOffset < total && eventsData.length === ITEMS_PER_PAGE);
        } catch (err) {
            console.error('이벤트 데이터 로딩 실패:', err);
            if (reset) {
                setEvents([]);
            }
        } finally {
            if (reset) {
                setLoading(false);
            }
            setIsLoadingMore(false);
        }
    }, [meInfo.id, selectedCategory, isLoadingMore, period, getMinDate, t]);

    // 메인 기관 ID 가져오기
    useEffect(() => {
        const meInfoData = JSON.parse(localStorage.getItem('meInfo') || '{}');
        setMeInfo(meInfoData);
        setMainOrganizationId(meInfoData.mainOrganizationId || null);
    }, []);

    // 이벤트 데이터 로드 (fetchEvents는 useCallback이지만 의존성 제외로 반복 호출 방지)
    useEffect(() => {
        if (meInfo?.id) {
            offsetRef.current = 0;
            setHasMore(true);
            fetchEvents(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 필터/기간 변경 시에만 재실행
    }, [selectedCategory, mainOrganizationId, meInfo?.id, period]);

    // 무한 스크롤 핸들러
    const handleScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore && !loading) {
            fetchEvents(false);
        }
    }, [hasMore, isLoadingMore, loading, fetchEvents]);

    // 당첨자 추가 로드 함수
    const fetchMoreWinners = useCallback(async (eventId) => {
        const eventIndex = events.findIndex(e => e.id === eventId);
        if (eventIndex === -1) return;

        const event = events[eventIndex];
        if (!event.winnersHasMore || event.isLoadingMoreWinners) return;

        // 로딩 상태 설정
        setEvents(prev => prev.map(e =>
            e.id === eventId ? { ...e, isLoadingMoreWinners: true } : e
        ));

        try {
            const winnersResponse = await eventAPI.getEventParticipations({
                eventId: eventId,
                expand: 'user',
                state: 2,
                offset: event.winnersOffset,
                limit: ITEMS_PER_PAGE,
            });

            const winnersData = winnersResponse?.data?.eventParticipations ||
                winnersResponse?.eventParticipations ||
                winnersResponse?.data || [];

            setEvents(prev => prev.map(e => {
                if (e.id !== eventId) return e;

                const newOffset = e.winnersOffset + winnersData.length;
                const newHasMore = newOffset < e.winnersTotal && winnersData.length === ITEMS_PER_PAGE;

                if (e.type === 0) {
                    // 추첨제: 개인별 당첨자 추가
                    const newWinners = winnersData.map(w => {
                        // loginId를 이메일로 사용
                        const email = w.user?.loginId || w.loginId || '';
                        const name = w.user?.name || w.name || '';
                        return {
                            name: name,
                            email: email,
                        };
                    });
                    return {
                        ...e,
                        winners: [...e.winners, ...newWinners],
                        winnersOffset: newOffset,
                        winnersHasMore: newHasMore,
                        isLoadingMoreWinners: false,
                    };
                } else if (e.type === 1) {
                    // 랭킹제: 원본 데이터 병합 후 재그룹화
                    const combinedRawData = [...e.winnersRawData, ...winnersData];
                    const orgMap = new Map();
                    const totalParticipationUsedPoint = e.totalParticipationUsedPoint || 0;

                    combinedRawData.forEach(w => {
                        const orgId = w.organizationId;
                        const orgName = w.organization?.name || w.organizationName || t('no_organization');

                        if (!orgMap.has(orgId)) {
                            orgMap.set(orgId, {
                                organizationId: orgId,
                                organizationName: orgName,
                                totalPoint: 0,
                                topUser: null,
                                topUserPoint: 0,
                                topUserEmail: '',
                            });
                        }

                        const orgData = orgMap.get(orgId);
                        const usedPoint = w.usedPoint || 0;
                        orgData.totalPoint += usedPoint;

                        if (usedPoint > orgData.topUserPoint) {
                            // loginId를 이메일로 사용
                            const email = w.user?.loginId || w.loginId || '';
                            const name = w.user?.name || w.name || '';
                            orgData.topUser = name;
                            orgData.topUserPoint = usedPoint;
                            orgData.topUserEmail = email;
                        }
                    });

                    const newRankingWinners = Array.from(orgMap.values())
                        .sort((a, b) => b.totalPoint - a.totalPoint)
                        .map((org, index) => ({
                            rank: index + 1,
                            organizationName: org.organizationName,
                            totalPoint: org.totalPoint,
                            percentage: totalParticipationUsedPoint > 0 ? Math.round((org.totalPoint / totalParticipationUsedPoint) * 100) : 0,
                            topUser: org.topUser,
                            topUserEmail: org.topUserEmail,
                            topUserPoint: org.topUserPoint,
                        }));

                    return {
                        ...e,
                        winnersRawData: combinedRawData,
                        rankingWinners: newRankingWinners,
                        winnersOffset: newOffset,
                        winnersHasMore: newHasMore,
                        isLoadingMoreWinners: false,
                    };
                }

                return { ...e, isLoadingMoreWinners: false };
            }));
        } catch (err) {
            console.error('당첨자 추가 로드 실패:', err);
            setEvents(prev => prev.map(e =>
                e.id === eventId ? { ...e, isLoadingMoreWinners: false } : e
            ));
        }
    }, [events, t]);

    // 당첨자 테이블 스크롤 핸들러
    const handleWinnersScroll = useCallback((e, eventId) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        if (scrollHeight - scrollTop - clientHeight < 50) {
            fetchMoreWinners(eventId);
        }
    }, [fetchMoreWinners]);

    const handleEventClick = (event) => {
        navigate('/events/user-detail', {
            state: { eventData: event }
        });
    };

    const handleApplyClick = async (event) => {
        // 랭킹제(type=1)이고 무소속인 경우 참여 불가
        if (event.type === 1 && !mainOrganizationId) {
            setErrorMessage(t('you_cannot_participate_without_an_affiliation'));
            setErrorPopupOpen(true);
            return;
        }

        setSelectedEvent(event);
        setApplyQuantity(1);
        setUserPoint(meInfo?.point || 0);

        // 기관 통계 가져오기 (랭킹제인 경우)
        if (event.type === 1 && mainOrganizationId) {
            try {
                const statisticsResponse = await eventAPI.getEventParticipationsStatistics({
                    eventId: event.id,
                    organizationId: mainOrganizationId,
                });
                const statisticsArray = statisticsResponse?.data?.eventParticipationsStatistics ||
                    statisticsResponse?.eventParticipationsStatistics || [];
                // 배열의 첫 번째 요소 사용
                const statistics = statisticsArray.length > 0 ? statisticsArray[0] : null;
                setOrganizationStatistics(statistics);
            } catch (err) {
                console.error('기관 통계 가져오기 실패:', err);
                setOrganizationStatistics(null);
            }

            // 주기관명 가져오기
            try {
                const orgResponse = await organizationAPI.getOrganizations({
                    id: mainOrganizationId,
                });
                const orgData = orgResponse?.data?.organizations?.[0] ||
                    orgResponse?.organizations?.[0] ||
                    null;
                setMainOrganizationName(orgData?.name || null);
            } catch (err) {
                console.error('기관명 가져오기 실패:', err);
                setMainOrganizationName(null);
            }
        } else {
            setOrganizationStatistics(null);
            setMainOrganizationName(null);
        }

        if (meInfo.point > 0) {
            setApplyDialogOpen(true);
        } else {
            setErrorMessage(t('insufficient_points'));
            setErrorPopupOpen(true);
        }
    };

    const handleQuantityChange = (delta) => {
        const newQuantity = applyQuantity + delta;
        const maxQuantity = userPoint || 1;
        const minQuantity = 1;
        setApplyQuantity(Math.max(minQuantity, Math.min(newQuantity, maxQuantity)));
    };

    const handleQuantityInputChange = (e) => {
        const value = parseInt(e.target.value) || 1;
        const maxQuantity = userPoint || 1;
        const minQuantity = 1;
        setApplyQuantity(Math.max(minQuantity, Math.min(value, maxQuantity)));
    };

    const handleApplySubmit = async () => {
        if (!selectedEvent) return;

        if (!meInfo.id) {
            console.error('사용자 ID를 가져올 수 없습니다.');
            return;
        }

        try {
            // participateEvent API 호출
            await eventAPI.participateEvent({
                userId: meInfo.id,
                eventId: selectedEvent.id,
                usedPoint: applyQuantity, // 응모 횟수만큼 포인트 사용
            });

            // 사용자 정보 다시 가져오기
            const userResponse = await userAPI.getMyInfo();
            const userData = userResponse?.data?.user;
            if (userData) {
                // localStorage에 저장
                const updatedMeInfo = { ...meInfo, ...userData };
                localStorage.setItem('meInfo', JSON.stringify(updatedMeInfo));
                // 상태 업데이트
                setMeInfo(updatedMeInfo);
                setUserPoint(updatedMeInfo.point || 0);
            }

            // 성공 시 팝업 닫기 및 데이터 새로고침
            setApplyDialogOpen(false);
            setOrganizationStatistics(null);
            setMainOrganizationName(null);
            // 이벤트 목록 다시 로드
            offsetRef.current = 0;
            setHasMore(true);
            fetchEvents(true);
        } catch (err) {
            console.error('응모하기 실패:', err);
            // TODO: 에러 메시지 표시
        }
    };

    return (
        <ContentArea sx={{ display: 'flex', flexDirection: 'column', height: '100vh', paddingBottom: 0 }}>
            {/* Category Toggle */}
            <Box sx={{
                display: 'flex',
                height: '40px',
                padding: '2px',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'stretch',
                backgroundColor: theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)',
                borderRadius: '15px',
                mx: 1.25,
                mb: 1.25,
            }}>
                <Toggle
                    options={categories}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    size="40"
                />
            </Box>

            {/* Period Select - 완료된 탭일 때만 표시 */}
            {selectedCategory === 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        padding: '10px',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '10px',
                        alignSelf: 'stretch',
                        px: 1.25,
                    }}
                >
                    <Dropdown
                        value={period}
                        onChange={option => setPeriod(option.value)}
                        options={periodOptions}
                        placeholder={t('select_period')}
                        sx={{ backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)' }}
                    />
                </Box>
            )}

            {/* Scrollable Content Container */}
            <Box
                onScroll={handleScroll}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingBottom: '100px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    px: 1.25
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                            {t('loading')}
                        </Typography>
                    </Box>
                ) : events.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                            {t('no_events')}
                        </Typography>
                    </Box>
                ) : selectedCategory === 0 ? (
                    // 진행중: 이미지 + 상세 정보
                    [events.map((event, index) => {
                        const imageUrl = event.imageUrl;
                        const isApplyableEvent = event.type === 0 || event.type === 1; // 추첨제 또는 랭킹제
                        return (
                            <Box
                                key={event.id || index}
                                sx={{
                                    width: '100%',
                                    pb: 2.5,
                                    mb: 2.5,
                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    '&:last-child': {
                                        borderBottom: 'none',
                                        mb: 0,
                                    }
                                }}
                            >
                                {/* Event Image */}
                                <Box
                                    onClick={() => handleEventClick(event)}
                                    sx={{
                                        cursor: 'pointer',
                                        width: '100%',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        mb: 1.25,
                                        minHeight: '243px',
                                    }}
                                >
                                    {imageUrl ? (
                                        <Box
                                            component="img"
                                            src={imageUrl}
                                            alt={event.name || t('event_image')}
                                            onError={(e) => {
                                                console.error('Image load error:', { imageUrl, eventId: event.id, eventName: event.name });
                                                e.target.style.display = 'none';
                                            }}
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '200px',
                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                            }}
                                        >
                                            <Typography>{t('no_image')}</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Event Details Below Image - 추첨제/랭킹제인 경우에만 표시 */}
                                {isApplyableEvent && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                            gap: 1.25,
                                        }}
                                    >
                                        {/* Participation Statistics */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {t('total_entries')}: {event.totalParticipationUsedPoint?.toLocaleString() || 0} {t('points')}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {t('my_entries')}: {(event.usedPoint || 0).toLocaleString()} {t('points')}
                                            </Typography>
                                        </Box>
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleApplyClick(event);
                                            }}
                                            variant="normal"
                                            sx={{
                                                height: '36px',
                                                minWidth: '100px',
                                                px: 2,
                                                fontSize: '14px',
                                                letterSpacing: '-0.42px',
                                            }}
                                        >
                                            {t('apply')}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        );
                    }),
                    // 로딩 인디케이터
                    isLoadingMore && (
                        <Box key="loading-more" sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                                {t('loading')}
                            </Typography>
                        </Box>
                    )
                    ].filter(Boolean)
                ) : (
                    // 완료된: 이미지 + 상세 정보 (진행중과 동일한 레이아웃)
                    [events.map((event, index) => {
                        const imageUrl = event.imageUrl;
                        const isApplyableEvent = event.type === 0 || event.type === 1; // 추첨제 또는 랭킹제
                        return (
                            <Box
                                key={event.id || index}
                                sx={{
                                    width: '100%',
                                    pb: 2.5,
                                    mb: 2.5,
                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    '&:last-child': {
                                        borderBottom: 'none',
                                        mb: 0,
                                    }
                                }}
                            >
                                {/* Event Image */}
                                <Box
                                    onClick={() => handleEventClick(event)}
                                    sx={{
                                        cursor: 'pointer',
                                        width: '100%',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        mb: 1.25,
                                        minHeight: '243px',
                                    }}
                                >
                                    {imageUrl ? (
                                        <Box
                                            component="img"
                                            src={imageUrl}
                                            alt={event.name || t('event_image')}
                                            onError={(e) => {
                                                console.error('Image load error:', { imageUrl, eventId: event.id, eventName: event.name });
                                                e.target.style.display = 'none';
                                            }}
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '200px',
                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                            }}
                                        >
                                            <Typography>{t('no_image')}</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Event Details Below Image - 추첨제/랭킹제인 경우에만 표시 */}
                                {isApplyableEvent && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            gap: '8px',
                                        }}
                                    >
                                        {/* Participation Statistics */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {t('total_entries')}: {event.totalParticipationUsedPoint?.toLocaleString() || 0} {t('points')}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {t('my_entries')}: {(event.usedPoint || 0).toLocaleString()} {t('points')}
                                            </Typography>
                                        </Box>

                                        {/* 당첨자 섹션 - 추첨제(type=0)인 경우 테이블 표시 */}
                                        {event.type === 0 && event.winners && event.winners.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '8px',
                                                    mt: 1,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                        letterSpacing: '-0.36px',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {t('winners')}
                                                </Typography>
                                                <TableContainer
                                                    component={Paper}
                                                    onScroll={(e) => handleWinnersScroll(e, event.id)}
                                                    sx={{
                                                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                        borderRadius: '10px',
                                                        border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                        overflowX: 'hidden',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto',
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                        boxSizing: 'border-box',
                                                        '& .MuiTable-root': {
                                                            width: '100% !important',
                                                            maxWidth: '100% !important',
                                                            minWidth: 0,
                                                            tableLayout: 'fixed',
                                                            boxSizing: 'border-box',
                                                        },
                                                    }}
                                                >
                                                    <Table size="small" stickyHeader sx={{
                                                        width: '100% !important',
                                                        maxWidth: '100% !important',
                                                        minWidth: 0,
                                                        tableLayout: 'fixed',
                                                        boxSizing: 'border-box',
                                                    }}>
                                                        <TableBody>
                                                            {event.winners.map((winner, winnerIndex) => {
                                                                // 이름 마스킹 (첫 글자만 표시)
                                                                const maskedName = winner.name ?
                                                                    winner.name.charAt(0) + '*'.repeat(Math.max(0, winner.name.length - 1)) :
                                                                    '';
                                                                // 이메일 마스킹 (@ 앞부분 일부만 표시)
                                                                const maskedEmail = winner.email ? (() => {
                                                                    const [localPart, domain] = winner.email.split('@');
                                                                    if (localPart && domain) {
                                                                        const visibleLength = Math.min(5, localPart.length);
                                                                        return localPart.substring(0, visibleLength) + '*@' + domain;
                                                                    }
                                                                    return winner.email;
                                                                })() : '';

                                                                return (
                                                                    <TableRow key={winnerIndex}>
                                                                        <TableCell sx={{
                                                                            color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                                            fontSize: '11px',
                                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                            padding: '6px 4px',
                                                                            wordBreak: 'break-word',
                                                                            overflowWrap: 'break-word',
                                                                            boxSizing: 'border-box',
                                                                            overflow: 'hidden',
                                                                        }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', }}>
                                                                                <Typography sx={{ fontSize: '11px' }}>
                                                                                    {maskedName}
                                                                                </Typography>
                                                                                <Typography sx={{ fontSize: '11px' }}>
                                                                                    {maskedEmail}
                                                                                </Typography>
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                            {event.isLoadingMoreWinners && (
                                                                <TableRow>
                                                                    <TableCell sx={{ textAlign: 'center', py: 2, boxSizing: 'border-box' }}>
                                                                        <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', fontSize: '11px' }}>
                                                                            {t('loading')}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}

                                        {/* 당첨자 섹션 - 랭킹제(type=1)인 경우 테이블 표시 */}
                                        {event.type === 1 && event.rankingWinners && event.rankingWinners.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '8px',
                                                    mt: 1,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                        letterSpacing: '-0.36px',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {t('winner_organizations')}
                                                </Typography>
                                                <TableContainer
                                                    component={Paper}
                                                    onScroll={(e) => handleWinnersScroll(e, event.id)}
                                                    sx={{
                                                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                        borderRadius: '10px',
                                                        border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                        overflowX: 'hidden',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto',
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                        boxSizing: 'border-box',
                                                        '& .MuiTable-root': {
                                                            width: '100% !important',
                                                            maxWidth: '100% !important',
                                                            minWidth: 0,
                                                            tableLayout: 'fixed',
                                                            boxSizing: 'border-box',
                                                        },
                                                    }}
                                                >
                                                    <Table size="small" stickyHeader sx={{
                                                        width: '100% !important',
                                                        maxWidth: '100% !important',
                                                        minWidth: 0,
                                                        tableLayout: 'fixed',
                                                        boxSizing: 'border-box',
                                                    }}>
                                                        <TableBody>
                                                            {event.rankingWinners.map((winner, winnerIndex) => {
                                                                // 이름 마스킹
                                                                const maskedName = winner.topUser ?
                                                                    winner.topUser.charAt(0) + '*'.repeat(Math.max(0, winner.topUser.length - 1)) :
                                                                    '';
                                                                // 이메일 마스킹
                                                                const maskedEmail = winner.topUserEmail ? (() => {
                                                                    const [localPart, domain] = winner.topUserEmail.split('@');
                                                                    if (localPart && domain) {
                                                                        const visibleLength = Math.min(5, localPart.length);
                                                                        return localPart.substring(0, visibleLength) + '*@' + domain;
                                                                    }
                                                                    return winner.topUserEmail;
                                                                })() : '';

                                                                return (
                                                                    <React.Fragment key={winnerIndex}>
                                                                        <TableRow>
                                                                            <TableCell sx={{
                                                                                color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                                                fontSize: '11px',
                                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                padding: '6px 4px',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                boxSizing: 'border-box',
                                                                                overflow: 'hidden',
                                                                            }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                                                                    <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
                                                                                        {winner.rank}{t('rank_suffix')}
                                                                                    </Typography>
                                                                                    <Typography sx={{ fontSize: '11px' }}>
                                                                                        {winner.organizationName}
                                                                                    </Typography>
                                                                                    <Typography sx={{ fontSize: '11px' }}>
                                                                                        {winner.totalPoint?.toLocaleString() || 0} {t('points')}
                                                                                    </Typography>
                                                                                    <Typography sx={{ fontSize: '11px' }}>
                                                                                        {winner.percentage}%
                                                                                    </Typography>
                                                                                </Box>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                        <TableRow>
                                                                            <TableCell sx={{
                                                                                color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                                                fontSize: '11px',
                                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                padding: '6px 4px',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                boxSizing: 'border-box',
                                                                                overflow: 'hidden',
                                                                            }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                                                                    <Typography sx={{ fontSize: '11px' }}>

                                                                                    </Typography>
                                                                                    <Typography sx={{ fontSize: '11px' }}>
                                                                                        {maskedName}
                                                                                    </Typography>
                                                                                    <Typography sx={{ fontSize: '11px' }}>
                                                                                        {maskedEmail}
                                                                                    </Typography>
                                                                                    <Typography sx={{ fontSize: '11px' }}>
                                                                                        {winner.topUserPoint?.toLocaleString() || 0} {t('points')}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                            {event.isLoadingMoreWinners && (
                                                                <TableRow>
                                                                    <TableCell sx={{ textAlign: 'center', py: 2, boxSizing: 'border-box' }}>
                                                                        <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', fontSize: '11px' }}>
                                                                            {t('loading')}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    }),
                    // 로딩 인디케이터
                    isLoadingMore && (
                        <Box key="loading-more" sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                                {t('loading')}
                            </Typography>
                        </Box>
                    )
                    ].filter(Boolean)
                )}
            </Box>

            {/* 응모하기 팝업 */}
            <Dialog
                open={applyDialogOpen}
                onClose={() => {
                    setApplyDialogOpen(false);
                    setOrganizationStatistics(null);
                    setMainOrganizationName(null);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.figma?.popupBG || '#1a1a1a',
                        borderRadius: '15px',
                        padding: '20px',
                    }
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    {/* 제목 */}
                    <Typography
                        sx={{
                            fontSize: '20px',
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 3,
                        }}
                    >
                        {t('apply_title')}
                    </Typography>

                    {/* 정보 섹션 */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        {/* 왼쪽 정보 */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                }}
                            >
                                {t('total_entries')}: {selectedEvent?.totalParticipationUsedPoint?.toLocaleString() || 0} {t('points')}
                            </Typography>
                            {/* 기관랭킹제(type=1)인 경우에만 표시 */}
                            {selectedEvent?.type === 1 && (
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    }}
                                >
                                    {t('my_affiliation')}: {(organizationStatistics?.organizationTotalUsedPoint || 0).toLocaleString()}{t('points')} ({mainOrganizationName || t('no_affiliation')})
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                }}
                            >
                                {t('my_entries')}: {(selectedEvent?.usedPoint || 0).toLocaleString()} {t('points')}
                            </Typography>
                        </Box>

                        {/* 오른쪽 포인트 정보 */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                }}
                            >
                                {t('available_points')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Point size={20} color="#FFCC00" />
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 700,
                                    }}
                                >
                                    {userPoint.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* 수량 조절 섹션 */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', mb: 3 }}>
                        <IconButton
                            onClick={() => handleQuantityChange(-10)}
                            sx={{
                                border: '2px solid',
                                borderColor: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '8px',
                                color: 'white',
                                width: '48px',
                                height: '40px',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>-10</Typography>
                        </IconButton>
                        <IconButton
                            onClick={() => handleQuantityChange(-1)}
                            sx={{
                                border: '2px solid',
                                borderColor: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '8px',
                                color: 'white',
                                width: '48px',
                                height: '40px',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>-1</Typography>
                        </IconButton>
                        <TextField
                            type="number"
                            value={applyQuantity}
                            onChange={handleQuantityInputChange}
                            inputProps={{
                                min: 1,
                                max: userPoint || 1,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    padding: '0',
                                    width: '100%',
                                }
                            }}
                            sx={{
                                width: '120px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    height: '40px',
                                    padding: '0',
                                    '& input': {
                                        padding: '0',
                                        textAlign: 'center',
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    },
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                },
                            }}
                        />
                        <IconButton
                            onClick={() => handleQuantityChange(1)}
                            sx={{
                                border: '2px solid',
                                borderColor: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '8px',
                                color: 'white',
                                width: '48px',
                                height: '40px',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>+1</Typography>
                        </IconButton>
                        <IconButton
                            onClick={() => handleQuantityChange(10)}
                            sx={{
                                border: '2px solid',
                                borderColor: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '8px',
                                color: 'white',
                                width: '48px',
                                height: '40px',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>+10</Typography>
                        </IconButton>
                    </Box>

                    {/* 응모 버튼 */}
                    <Button
                        onClick={handleApplySubmit}
                        variant="normal"
                        size="large"
                        sx={{
                            width: '100%',
                        }}
                    >
                        {t('apply')}
                    </Button>
                </DialogContent>
            </Dialog>

            {/* 에러 팝업 */}
            <Popup
                open={errorPopupOpen}
                message={errorMessage}
                onConfirm={() => setErrorPopupOpen(false)}
            />
        </ContentArea>
    );
};

export default EventUser;
